import React, { useEffect, useState, createContext, useContext, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { seedInitialAdmin } from './services/seedAdmin';
import { getUserRole } from './services/adminService';
import { logPageView } from './services/analyticsService';
import Navigation from './components/layout/Navigation/Navigation';
import Footer from './components/layout/Footer/Footer';
import './styles/ubunited.css';

// Lazy-load all pages — only the visited page's code is downloaded
const Pickleball = lazy(() => import('./pages/Pickleball/Pickleball'));
const Blog = lazy(() => import('./pages/Blog/Blog'));
const BlogPost = lazy(() => import('./pages/Blog/BlogPost'));
const Videos = lazy(() => import('./pages/Videos/Videos'));
const Rules = lazy(() => import('./pages/Rules/Rules'));
const GoverningBodies = lazy(() => import('./pages/GoverningBodies/GoverningBodies'));
const Competitions = lazy(() => import('./pages/Competitions/Competitions'));
const Messaging = lazy(() => import('./pages/Groups/Groups'));
const GroupsHome = lazy(() => import('./pages/GroupsHome/GroupsHome'));
const Shop = lazy(() => import('./pages/Shop/Shop'));
const About = lazy(() => import('./pages/About/About'));
const Contact = lazy(() => import('./pages/Contact/Contact'));
const Auth = lazy(() => import('./pages/Auth/Auth'));
const Admin = lazy(() => import('./pages/Admin/Admin'));
const Profile = lazy(() => import('./pages/Profile/Profile'));
const Terms = lazy(() => import('./pages/Terms/Terms'));
const Privacy = lazy(() => import('./pages/Privacy/Privacy'));
const CommunityGuidelines = lazy(() => import('./pages/CommunityGuidelines/CommunityGuidelines'));

const ADMIN_PANEL_ROLES = [
  'super_admin', 'portal_admin', 'ambassador',
  'sport_ambassador', 'content_moderator', 'member_moderator',
];

const NO_ROLE = { role: null, isAdmin: false, isPortalAdmin: false, isAmbassador: false, hasAdminAccess: false };

const RoleContext = createContext(NO_ROLE);
export const useRole = () => useContext(RoleContext);

const RoleProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [role, setRole] = useState(NO_ROLE);

  useEffect(() => {
    const init = async () => {
      if (!currentUser) {
        setRole(NO_ROLE);
        return;
      }
      await seedInitialAdmin(currentUser);
      const userRole = await getUserRole(currentUser.uid, currentUser.email);
      const r = userRole?.role || null;
      setRole({
        role: r,
        isAdmin: r === 'super_admin',
        isPortalAdmin: r === 'portal_admin' || r === 'super_admin',
        isAmbassador: r === 'ambassador' || r === 'super_admin',
        hasAdminAccess: ADMIN_PANEL_ROLES.includes(r),
      });
    };
    init();
  }, [currentUser]);

  return <RoleContext.Provider value={role}>{children}</RoleContext.Provider>;
};

const PageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    logPageView(location.pathname);
  }, [location.pathname]);
  return null;
};

const PageLoader = () => (
  <section style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh', color: '#888' }}>
    Loading...
  </section>
);

const App = () => {
  return (
    <AuthProvider siteId="ubunited">
      <RoleProvider>
        <CartProvider>
          <Router>
            <PageTracker />
            <Navigation />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Pickleball />} />
                <Route path="/pickleball" element={<Navigate to="/" replace />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:postId" element={<BlogPost />} />
                <Route path="/videos" element={<Videos />} />
                <Route path="/resources/rules" element={<Rules />} />
                <Route path="/resources/governing-bodies" element={<GoverningBodies />} />
                <Route path="/resources/competitions" element={<Competitions />} />
                <Route path="/messaging" element={<Messaging />} />
                <Route path="/groups" element={<GroupsHome />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/community-guidelines" element={<CommunityGuidelines />} />
              </Routes>
            </Suspense>
            <Footer />
          </Router>
        </CartProvider>
      </RoleProvider>
    </AuthProvider>
  );
};

export default App;
